import React, { useCallback, useEffect, useRef, useState } from "react";
import { useInView } from "react-intersection-observer";
import { useApiClient } from "Services/API/useApiClient";
import { BlogItem, BlogTag, BlogsClient } from "Services/API/API";
import { timeToDuration } from "Utils/datetime";
import initCarousel from "../../initCarousel";

const MAX_RESOURCES = 9;

const getReadTime = (readTime?: string) => {
   const minutes = readTime ? timeToDuration(readTime, "minutes") : 0;
   return Math.ceil(minutes) + " min read";
};

const ResourcesSection: React.FC = () => {
   const [resources, setResources] = useState<BlogItem[]>([]);
   const blogClient = useApiClient(BlogsClient);
   const { ref, inView } = useInView({
      triggerOnce: true,
      rootMargin: "500px",
   });

   const getResources = useCallback(
      async (signal?: AbortSignal): Promise<BlogItem[]> => {
         try {
            const blogModel = await blogClient.byTag(
               BlogTag.Homefeature,
               signal
            );
            return blogModel?.items?.slice(0, MAX_RESOURCES) ?? [];
         } catch (err) {
            console.log(err);
            return [];
         }
      },
      [blogClient]
   );

   useEffect(() => {
      let isMounted = true;
      const abortController = new AbortController();

      const fetchResources = async () => {
         const r = await getResources(abortController.signal);
         if (isMounted) {
            setResources(r);
         }
      };

      if (inView) {
         fetchResources();
      }

      return () => {
         isMounted = false;
         abortController.abort();
      };
   }, [getResources, inView]);

   const swiperRef = useRef<HTMLDivElement>(null);
   useEffect(() => {
      if (resources.length == 0 || !swiperRef.current) {
         return;
      }
      const swiper = initCarousel(swiperRef.current, { loop: false, gap: { mobile: 16, desktop: 24 } });
      return () => swiper?.destroy();
   }, [resources]);

   return (
      <div ref={ref}>
         {resources.length > 0 && (
            <div className="hp3-carousel hp3-resources-carousel">
               <div className="swiper hp3-carousel-viewport" ref={swiperRef} aria-label="Resources">
                  <div className="swiper-wrapper">
                     {resources.map((item) => (
                        <a key={item.link ?? item.title} href={item.link} className="swiper-slide hp3-res-card">
                           <img className="hp3-res-card-img" src={item.imgSource} alt={item.imgAlt || item.title || ""} loading="lazy" draggable={false} />
                           <span className="hp3-res-card-body">
                              <span className="hp3-res-read-time">{getReadTime(item.readTime)}</span>
                              <span className="hp3-res-title">{item.title}</span>
                              <span className="hp3-res-excerpt">{item.summary}</span>
                              <span className="hp3-res-link">Read more →</span>
                           </span>
                        </a>
                     ))}
                  </div>
               </div>
               <div className="hp3-arrow-row">
                  <button type="button" className="hp3-arrow" aria-label="Previous" data-carousel-prev>
                     <svg viewBox="0 0 18 18" fill="none" aria-hidden="true">
                        <path d="M14 9H4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                        <path d="M8 4L3 9L8 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                     </svg>
                  </button>
                  <button type="button" className="hp3-arrow" aria-label="Next" data-carousel-next>
                     <svg viewBox="0 0 18 18" fill="none" aria-hidden="true">
                        <path d="M4 9H14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                        <path d="M10 4L15 9L10 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                     </svg>
                  </button>
               </div>
            </div>
         )}
      </div>
   );
};

export default ResourcesSection;
